import { Download, Trash2, FileText } from 'lucide-react';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function CategoryBadge({ category }) {
  return (
    <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
      {category}
    </span>
  );
}

export default function DocumentTable({ documents, onView, onDelete }) {
  if (documents.length === 0) {
    return (
      <div className="px-6 py-16 text-center">
        <div className="mx-auto h-11 w-11 rounded-full bg-[#00384D]/5 flex items-center justify-center mb-4">
          <FileText className="h-5 w-5 text-[#4EA6B3]" strokeWidth={1.75} />
        </div>
        <p className="text-[#00384D] font-medium text-sm">No documents yet</p>
        <p className="text-slate-400 text-sm mt-1">
          Uploaded district and church reports will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <table className="w-full text-sm hidden md:table">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
            <th className="px-6 py-3 font-medium">Document</th>
            <th className="px-6 py-3 font-medium">Category</th>
            <th className="px-6 py-3 font-medium">Church</th>
            <th className="px-6 py-3 font-medium">Year</th>
            <th className="px-6 py-3 font-medium">Uploaded</th>
            <th className="px-6 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {documents.map((doc) => (
            <tr key={doc.id} className="hover:bg-slate-50/80 transition-colors">
              <td className="px-6 py-3.5">
                <p className="font-medium text-[#00384D] leading-snug">{doc.title}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 tracking-wide">
                  {doc.file_type?.toUpperCase()}
                </p>
              </td>
              <td className="px-6 py-3.5">
                <CategoryBadge category={doc.category} />
              </td>
              <td className="px-6 py-3.5 text-slate-600">{doc.church_name || 'District-wide'}</td>
              <td className="px-6 py-3.5 text-slate-600">{doc.year || '—'}</td>
              <td className="px-6 py-3.5 text-slate-500">{formatDate(doc.created_at)}</td>
              <td className="px-6 py-3.5">
                <div className="flex justify-end gap-1">
                  <button
                    onClick={() => onView(doc)}
                    className="text-slate-400 hover:text-[#007A96] hover:bg-[#007A96]/8 rounded-md p-1.5 transition-colors"
                    aria-label="View document"
                  >
                    <Download className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                  <button
                    onClick={() => onDelete(doc)}
                    className="text-slate-400 hover:text-[#C21E26] hover:bg-[#C21E26]/8 rounded-md p-1.5 transition-colors"
                    aria-label="Delete document"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile stacked cards */}
      <ul className="divide-y divide-slate-100 md:hidden">
        {documents.map((doc) => (
          <li key={doc.id} className="px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-[#00384D] text-sm leading-snug">{doc.title}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <CategoryBadge category={doc.category} />
                  <span className="text-xs text-slate-400">{doc.church_name || 'District-wide'}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1.5">
                  {doc.year || '—'} · {formatDate(doc.created_at)}
                </p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  onClick={() => onView(doc)}
                  className="text-slate-400 hover:text-[#007A96] hover:bg-[#007A96]/8 rounded-md p-1.5"
                  aria-label="View"
                >
                  <Download className="h-4 w-4" strokeWidth={1.75} />
                </button>
                <button
                  onClick={() => onDelete(doc)}
                  className="text-slate-400 hover:text-[#C21E26] hover:bg-[#C21E26]/8 rounded-md p-1.5"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
